import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { cn, getErrorBorderClass } from '@/lib';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordRequirements } from '@/components/password-requirements';

export type BusinessRegistrationFormProps = React.ComponentProps<'form'> & {
  email: string;
  password: string;
  confirmPassword: string;
  isLoading: boolean;
  retryAfterSeconds: number;
  hasError?: boolean;
  errorMessage?: string | null;
  passwordsMatch: boolean;
  isPasswordValid: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: () => void;
};

export function BusinessRegistrationForm({
  className,
  email,
  password,
  confirmPassword,
  isLoading,
  retryAfterSeconds,
  hasError,
  errorMessage,
  passwordsMatch,
  isPasswordValid,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  ...props
}: BusinessRegistrationFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isFormValid = email.trim() && password.trim() && confirmPassword.trim() && passwordsMatch && isPasswordValid;

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      onSubmit={(e) => (e.preventDefault(), onSubmit())}
      {...props}
    >
      {errorMessage && (
        <div className="bg-destructive/15 text-destructive rounded-md p-3 text-sm font-medium">{errorMessage}</div>
      )}

      <div className="grid gap-4">
        <div className="grid gap-3">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            autoComplete="email"
            className={getErrorBorderClass(hasError)}
            autoFocus
            required
          />
        </div>

        <div className="grid gap-3">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              autoComplete="new-password"
              className={cn('pr-10', getErrorBorderClass(hasError))}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {password && <PasswordRequirements password={password} />}
        </div>

        <div className="grid gap-3">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              autoComplete="new-password"
              className={cn('pr-10', getErrorBorderClass(hasError))}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((s) => !s)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {confirmPassword && !passwordsMatch && <p className="text-sm text-red-500">Passwords do not match</p>}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading || retryAfterSeconds > 0 || !isFormValid}>
          {isLoading
            ? 'Creating account...'
            : retryAfterSeconds > 0
              ? `Try again in ${retryAfterSeconds}s`
              : 'Create account'}
        </Button>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">Already have an account? </span>
          <Link to="/login" className="text-primary hover:underline">
            Login
          </Link>
        </div>
      </div>
    </form>
  );
}
